/**
 * The rail's layout and how it is read. Pure — no React, no store.
 *
 * Top to bottom the rail holds rows, sections and spaces. A row is a starred
 * destination id, or a parent row that carries sub-items (other destination ids,
 * one level deep). A section is a label and the rows it holds; it moves as one
 * unit. A space is a gap. Rows, sections and spaces can sit anywhere at the top
 * level; sections hold rows only.
 *
 * Every row, section and space has an id, and every list of them is a
 * *container*: `ROOT` for the top level, a section's id for its rows, a parent
 * row's id for its sub-items. The edits live in `railLayoutOps.ts`.
 *
 * Spec: docs/plans/2026-09-16-nav-rail-sections-design.md §1 (revisions 2 and 3).
 */
import { destinationById } from './destinations';

/** A row with sub-items. Stored only while it has some; a parent with none is a plain id. */
export interface ParentRow {
  kind: 'row';
  id: string;
  children: string[];
}

/** A starred row: a destination id, or a parent row. */
export type RowEntry = string | ParentRow;

/** A section: an id stable across renames, the user's label, and its rows in order. */
export interface RailSection {
  id: string;
  label: string;
  ids: RowEntry[];
}

/** A gap between top-level items. */
export interface RailSpace {
  kind: 'space';
  id: string;
}

/** One top-level rail item. */
export type RailItem = RowEntry | RailSection | RailSpace;

/** The whole rail, top to bottom. */
export type RailLayout = RailItem[];

/** Container key for the top level. */
export const ROOT = 'root';

/** Longest section label kept; a sidebar row truncates well before this. */
export const SECTION_LABEL_MAX = 40;
/** The label a section starts with, and the fallback for an empty one. */
export const NEW_SECTION_LABEL = 'New section';

/**
 * Destination ids that no longer exist → the id that absorbed them. Profile and
 * the Assistant/Administration hubs became the one Settings page (nav spec
 * §4.6); a star on any of them is a star on Settings, not a dropped star.
 */
const RETIRED: Record<string, string> = {
  assistant: 'settings',
  administration: 'settings',
  profile: 'settings',
};

/** Whether an item is a section. */
export function isSection(item: RailItem): item is RailSection {
  return typeof item === 'object' && !('kind' in item);
}

/** Whether an item is a space. */
export function isSpace(item: RailItem): item is RailSpace {
  return typeof item === 'object' && 'kind' in item && item.kind === 'space';
}

/** Whether an item is a parent row. */
export function isParent(item: RailItem): item is ParentRow {
  return typeof item === 'object' && 'kind' in item && item.kind === 'row';
}

/** Whether an item is a row (plain or parent). */
export function isRow(item: RailItem): item is RowEntry {
  return typeof item === 'string' || isParent(item);
}

/** The id of any item. */
export function idOf(item: RailItem): string {
  return typeof item === 'string' ? item : item.id;
}

/** A row's sub-items (none for a plain id). */
export function childrenOf(entry: RowEntry): string[] {
  return typeof entry === 'string' ? [] : entry.children;
}

/** A row with the given sub-items — a plain id when there are none. */
export function withChildren(id: string, children: string[]): RowEntry {
  return children.length > 0 ? { kind: 'row', id, children } : id;
}

/** A stored id as a live destination id: retired ids aliased, anything unknown undefined. */
function liveId(id: unknown): string | undefined {
  if (typeof id !== 'string') return undefined;
  const aliased = RETIRED[id] ?? id;
  return destinationById(aliased) ? aliased : undefined;
}

/** Destination ids cleaned: retired ids aliased, unknown ids dropped with a warning, duplicates collapsed. */
export function cleanIds(ids: readonly unknown[]): string[] {
  const live = ids.flatMap(id => {
    const resolved = liveId(id);
    if (resolved === undefined) {
      console.warn(`[nav.stars] dropping unknown destination id: ${String(id)}`);
    }
    return resolved === undefined ? [] : [resolved];
  });
  return [...new Set(live)];
}

/** A label trimmed and capped; empty when nothing is left. */
export function cleanLabel(label: string): string {
  return label.trim().slice(0, SECTION_LABEL_MAX);
}

/** A stored value as a plain object, or undefined. */
function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** The fields of a stored header object, when it has a usable id and label. */
function headerFields(
  value: unknown
): { id: string; label: string; raw: Record<string, unknown> } | undefined {
  const raw = asRecord(value);
  if (!raw || typeof raw.id !== 'string' || raw.id === '' || typeof raw.label !== 'string') {
    return undefined;
  }
  const label = cleanLabel(raw.label);
  return label ? { id: raw.id, label, raw } : undefined;
}

/**
 * The first stored format (#3354) was flat: `{id, label}` headers between ids,
 * each header owning the rows below it until the next one. Convert it to
 * sections; a value already in the nested format passes through untouched.
 */
function fromFlat(stored: readonly unknown[]): unknown[] {
  const isFlatHeader = (v: unknown) =>
    headerFields(v) !== undefined && !Array.isArray((v as { ids?: unknown }).ids);
  if (!stored.some(isFlatHeader)) return [...stored];
  const out: unknown[] = [];
  let open: { id: string; label: string; ids: unknown[] } | undefined;
  for (const entry of stored) {
    const header = isFlatHeader(entry) ? headerFields(entry) : undefined;
    if (header) out.push((open = { id: header.id, label: header.label, ids: [] }));
    else if (open && typeof entry === 'string') open.ids.push(entry);
    else out.push(entry);
  }
  return out;
}

/** Tracks what has been placed, so a reconcile keeps only the first of each id. */
class Placement {
  private readonly seen = new Set<string>();

  /** `inRoot`: the destination ids a row must be starred under to be placed. */
  constructor(private readonly inRoot: ReadonlySet<string>) {}

  /** A destination id, if it may be placed now; records it as placed. */
  private destination(value: unknown): string | undefined {
    const id = liveId(value);
    if (id === undefined || !this.inRoot.has(id) || this.seen.has(id)) return undefined;
    this.seen.add(id);
    return id;
  }

  /** A row — a plain id, or a parent with its surviving sub-items. */
  row(value: unknown): RowEntry | undefined {
    const raw = asRecord(value);
    if (!raw) return this.destination(value);
    if (raw.kind !== 'row') return undefined;
    const id = this.destination(raw.id);
    if (id === undefined) return undefined;
    const children = Array.isArray(raw.children) ? raw.children : [];
    return withChildren(
      id,
      children.flatMap(child => this.destination(child) ?? [])
    );
  }

  /** A section, if its id is new; its rows placed in turn. */
  section(value: unknown): RailSection | undefined {
    const header = headerFields(value);
    if (!header || !Array.isArray(header.raw.ids) || this.seen.has(header.id)) return undefined;
    this.seen.add(header.id);
    return {
      id: header.id,
      label: header.label,
      ids: header.raw.ids.flatMap(v => this.row(v) ?? []),
    };
  }

  /** A space, if its id is new. */
  space(value: unknown): RailSpace | undefined {
    const raw = asRecord(value);
    if (raw?.kind !== 'space' || typeof raw.id !== 'string' || this.seen.has(raw.id))
      return undefined;
    this.seen.add(raw.id);
    return { kind: 'space', id: raw.id };
  }

  /** Any top-level item. */
  item(value: unknown): RailItem | undefined {
    const kind = asRecord(value)?.kind;
    if (kind === 'space') return this.space(value);
    if (typeof value === 'string' || kind === 'row') return this.row(value);
    return this.section(value);
  }

  /** Whether a destination id has been placed. */
  has(id: string): boolean {
    return this.seen.has(id);
  }
}

/**
 * The stored layout reconciled with the root id set (spec §1.1). Malformed
 * entries, unknown or duplicate ids and stars `rootIds` no longer holds are
 * dropped; stars `rootIds` holds that the layout lacks are appended, ungrouped.
 * A client that predates sections writes only `root`, so this is how its
 * changes land.
 */
export function reconcileLayout(stored: unknown, rootIds: readonly string[]): RailLayout {
  if (!Array.isArray(stored)) return [...rootIds];
  const placement = new Placement(new Set(rootIds));
  const out = fromFlat(stored).flatMap(value => placement.item(value) ?? []);
  return [...out, ...rootIds.filter(id => !placement.has(id))];
}

/** A row's destination ids: itself, then its sub-items. */
function rowStars(entry: RowEntry): string[] {
  return [idOf(entry), ...childrenOf(entry)];
}

/** The destination ids, in rail order. */
export function starsOf(layout: RailLayout): string[] {
  return layout.flatMap(item => {
    if (isSpace(item)) return [];
    return isSection(item) ? item.ids.flatMap(rowStars) : rowStars(item);
  });
}

/** A copy of `items` with the item at `from` moved to `to`. */
export function moveWithin<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

/** The top-level index of an item id (a section, space, or top-level row). */
export function indexOfItem(layout: RailLayout, id: string): number {
  return layout.findIndex(item => idOf(item) === id);
}

/** The key of the container directly holding item `id`: `ROOT`, a section id, or a parent row id. */
export function containerOf(layout: RailLayout, id: string): string | undefined {
  for (const item of layout) {
    if (idOf(item) === id) return ROOT;
    if (isRow(item) && childrenOf(item).includes(id)) return idOf(item);
    if (!isSection(item)) continue;
    for (const entry of item.ids) {
      if (idOf(entry) === id) return item.id;
      if (childrenOf(entry).includes(id)) return idOf(entry);
    }
  }
  return undefined;
}

/** The index of the top-level item that holds `id` at any depth, or -1. */
export function topIndexOf(layout: RailLayout, id: string): number {
  return layout.findIndex(
    item =>
      idOf(item) === id ||
      (isRow(item) && childrenOf(item).includes(id)) ||
      (isSection(item) &&
        item.ids.some(entry => idOf(entry) === id || childrenOf(entry).includes(id)))
  );
}

/** What kind of container `key` names, if any: a sub-item or a space holds nothing. */
export function containerKind(
  layout: RailLayout,
  key: string
): 'root' | 'section' | 'children' | undefined {
  if (key === ROOT) return 'root';
  const holder = containerOf(layout, key);
  if (holder === ROOT) {
    const item = layout[indexOfItem(layout, key)];
    if (item !== undefined && isSection(item)) return 'section';
    return item !== undefined && isRow(item) ? 'children' : undefined;
  }
  // A row inside a section can hold sub-items; a sub-item (held by a parent row) cannot.
  return holder !== undefined && containerKind(layout, holder) === 'section'
    ? 'children'
    : undefined;
}

/** The items of container `key` (untyped: top-level items, section rows, or sub-item ids). */
export function itemsIn(layout: RailLayout, key: string): RailItem[] {
  if (key === ROOT) return layout;
  for (const item of layout) {
    if (isSection(item)) {
      if (item.id === key) return item.ids;
      const parent = item.ids.find(entry => idOf(entry) === key);
      if (parent !== undefined) return childrenOf(parent);
    } else if (isRow(item) && idOf(item) === key) {
      return childrenOf(item);
    }
  }
  return [];
}

/** A parent row with its sub-items replaced by `fn`, when it is the container `key`. */
function updateRow(entry: RowEntry, key: string, fn: (items: RailItem[]) => RailItem[]): RowEntry {
  if (idOf(entry) !== key) return entry;
  return withChildren(idOf(entry), fn(childrenOf(entry)) as string[]);
}

/** `layout` with container `key`'s items replaced by `fn(items)`. */
export function updateContainer(
  layout: RailLayout,
  key: string,
  fn: (items: RailItem[]) => RailItem[]
): RailLayout {
  if (key === ROOT) return fn(layout);
  return layout.map(item => {
    if (isSpace(item)) return item;
    if (!isSection(item)) return updateRow(item, key, fn);
    if (item.id === key) return { ...item, ids: fn(item.ids) as RowEntry[] };
    return { ...item, ids: item.ids.map(entry => updateRow(entry, key, fn)) };
  });
}
